import { unstable_usePrompt, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  useAutoExtract,
  useCanceAutoExtract,
  useCreateCustomTable,
  useCreateGlobalCustomTable,
  useCreateSheetExtract,
  useDeleteCustomTable,
  useDeleteGlobalCustomTable,
  useDeleteSheet,
  useDeleteSheetExtract,
  useGetAllStatusExtractions,
  useGetCategories,
  useGetPdfRegionSelections,
  useGetFileExtract,
  useGetHeaderFooter,
  useGetReplicateExtraction,
  useGetSheet,
  useGetWorkflowById,
  useGetWorkflowExtract,
  useReplicateAutoExtract,
  useSetHeaderFooter,
  useDeletePdfRegionSelection,
  useUpsertPdfRegionSelection,
  useUpdateGlobalCustomTable,
  useUpdateSheetExtract,
  useUpdateTableStructure
} from '../../services/workflow.services';
import GridTable from './components/GridTable';
import Header from './components/Header';
import LookupTables from './components/LookupTables';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { extractApiError } from '../../utils';
import { useDebounce } from '../../hooks/useDebounce';
import FundMappingModal from './components/FundMappingModal';
import { useDeletePeriodFile, usePeriodById, usePeriodFiles } from '../../services/period.service';
import { useGetFundMappings, useSetPasswordToFile } from '../../services/funds.service';
import { useTableNavigation } from '../../hooks/useTableNavigation';
import { useSignalRListener } from '../../hooks/useSignalR';
import { SIGNALR_LISTENERS } from '../../config/signalr';
import extractionStore from './store';
import { observer } from 'mobx-react-lite';
import { deleteActions, getUpdatedExtractedFileFunds } from './utils';
import usePromptUnloadPage from '../../hooks/usePromptUnloadPage';
import { useUserGroups, useUserGroupsCurrentUserAdminState } from '../../services/user.service';
import { permissionPolicies } from '../../permissions/policies';
import { Box, Button, Flex, Overlay, Modal, Text } from '@mantine/core';
import { SemiExtractionDrawer } from './components/SemiExtractionDrawer';
import { useDisclosure } from '@mantine/hooks';
import CustomTableDrawer from './components/CustomTableDrawer';
import { queryClient } from '../../config/queryClient';
import { GET_CUSTOM_FIELDS, GET_TRANSFORM } from '../../consts/queryKeys';
import { NewDeleteConfirmModal } from '../../components/NewDeleteConfirmModal';
import agentStore from '../../store/agent';
import { WEB_PAGE_STATE_ENUMS } from '../../components/ChatWidget/constants';
import { useWorkflowChrome } from '../../components/Layout/WorkflowChromeContext';
import transformationStore from './transformation.store';
import { REPORT_TYPES } from '../../consts/reportTypes';
import { PasswordModal } from '../Project/Funds/components/PasswordModal';
import { protectedFile } from './components/Footer/icons';

export const CACHE_STALE_TIME = 2 * 60 * 1000; // 2 minutes

const escapeCsvValue = value => {
  if (value === null || value === undefined) return '';
  const stringValue = String(value);
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const buildCsvFromRows = rows => {
  if (!rows?.length) return '';

  const columns = Object.keys(rows[0] || {});
  const header = columns.map(escapeCsvValue).join(',');
  const body = rows
    .map(row => columns.map(column => escapeCsvValue(row?.[column])).join(','))
    .join('\n');

  return `${header}\n${body}`;
};

const DataExtraction = observer(() => {
  const rootId = 'data-extraction-page';
  const { id, periodId } = useParams();
  const navigate = useNavigate();
  const excelTableRef = useRef();
  const [searchParams, setSearchParams] = useSearchParams();

  // Lookup Tables tab visibility lives in the workflow stepper (gated by the
  // `lookup_table` company flag inside `useWorkflowStages`). The page itself
  // trusts whatever tab the URL carries — backend APIs enforce real access.
  const { data: currentUser } = useUserGroupsCurrentUserAdminState();

  // Normalize `?tab=` on read so the first render — before the URL self-heal
  // effect below fires — never renders the page with an unknown tab value
  // (which would break GridTable / data selection logic).
  const rawTab = searchParams.get('tab') || 'extraction';
  const isAllowedTab =
    rawTab === 'extraction' || rawTab === 'transformation' || rawTab === 'lookup_tables';
  const activeTab = isAllowedTab ? rawTab : 'extraction';

  const extractId = searchParams.get('extractId');
  const sheetId = searchParams.get('sheetId');
  const fileId = searchParams.get('fileId');
  const categoryName = searchParams.get('category');
  const extractedTransformationTable = searchParams.get('extractedTransformationTable');
  const pdfView = searchParams.get('pdfView') || 'table';

  const setActiveTab = val =>
    setSearchParams(init => {
      init.set('tab', val);
      return init;
    });

  // Self-heal the URL when `?tab=` is invalid (typo like `?tab=lookup`,
  // `?tab=foo`). Compares the *raw* URL value, not the normalized `activeTab`
  // (which is already collapsed to a valid one for the current render).
  useEffect(() => {
    if (rawTab !== activeTab) {
      setActiveTab('extraction');
    }
  }, [rawTab, activeTab]);

  const setSheetId = val =>
    setSearchParams(init => {
      init.set('sheetId', val);
      init.delete('extractId');
      init.delete('category');
      return init;
    });

  const setFileId = val =>
    setSearchParams(init => {
      init.set('fileId', val);
      init.delete('sheetId');
      init.delete('category');
      return init;
    });

  const setExtractId = val => {
    if (!val) return;

    setSearchParams(init => {
      init.set('extractId', val);
      init.delete('category');
      return init;
    });
  };

  const setCategoryName = val => {
    if (!val) return;

    setSearchParams(init => {
      init.set('category', val);

      return init;
    });
  };

  const setExtractedTransformationTable = val => {
    if (!val && val !== '') return;

    setSearchParams(init => {
      if (val === '') init.delete('extractedTransformationTable');
      else init.set('extractedTransformationTable', val);
      return init;
    });
  };

  const setPdfView = val => {
    setSearchParams(init => {
      if (!val) init.delete('pdfView');
      else init.set('pdfView', val);
      return init;
    });
  };

  const [openedSemiExtract, { open: onOpenSemiExtract, close: onCloseSemiExtract }] =
    useDisclosure(false);

  const [term, setTerm] = useState('');
  const [openTemplates, setOpenTemplates] = useState(false);
  const [foundItems, setFoundItems] = useState([]);
  const [activeSearchedIndex, setActiveSearchedIndex] = useState(0);
  const [action, setAction] = useState();
  const [activeFundChildValue, setActiveFundChildValue] = useState(null);
  const [isPauseStatusReq, setIsPauseStatusReq] = useState(false);
  const [fundMappingAction, setFundMappingAction] = useState(null);
  const [actionDelete, setActionDelete] = useState(null);
  const [previuseCategory, setPreviusCategory] = useState({});
  const previousExtractionState = useRef({ categoryName: null, extractId: null });
  const [transformationData, setTransformationData] = useState();
  const [hasChanges, setHasChanges] = useState(false);
  const [foundCell, setFoundCell] = useState(null);

  const [selecetedCustomTable, setSelectedCustomTable] = useState(null);

  const [isOpenFundMapping, setIsOpenFundMapping] = useState(false);
  const [isOpenCustomTableDrawer, setIsOpenCustomTableDrawer] = useState(false);
  const [isOpenDeleteCustomTableModal, setIsOpenDeleteCustomTableModal] = useState(false);
  const [isOpenUpdateCustomTableDrawer, setIsOpenUpdateCustomTableDrawer] = useState(false);
  const [selectedProtectedFile, setSelectedProtectedFile] = useState(null);

  const toggleUpdateCustomTableDrawer = _table => {
    setSelectedCustomTable(_table || null);
    setIsOpenUpdateCustomTableDrawer(prev => !prev);
  };

  const toggleCustomTableDrawer = () => setIsOpenCustomTableDrawer(prev => !prev);

  const toggleDeleteCustomTableModal = () => setIsOpenDeleteCustomTableModal(prev => !prev);

  const createCustomTable = useCreateCustomTable();
  const createGlobalCustomTable = useCreateGlobalCustomTable();
  const updateCustomTable = useUpdateGlobalCustomTable();
  const deleteCustomTable = useDeleteCustomTable();
  const deleteGlobalCustomTable = useDeleteGlobalCustomTable();

  const {
    category,
    isLockedPeriod,
    setIsLockedPeriod,
    setFunds,
    selectedFund,
    setSelectedFund,
    computeSubmitBody,
    pickedFund,
    computeFunds,
    setCategory,
    setPickedFund,
    pickedChildFund,
    setPickedChildFund,
    selectedExtract,
    historyState: { funds },
    set,
    undo,
    redo,
    canRedo,
    canUndo,
    clear,
    setSelectedExtract,
    setExtractionType,
    setReportType,
    extractionType
  } = extractionStore;

  const onSelectCustomTable = val => {
    setSelectedCustomTable(val);
    setIsOpenDeleteCustomTableModal(true);
  };

  const onDeleteCustomTable = callback => {
    if (project?.report_type === REPORT_TYPES.CUSTOM) {
      deleteGlobalCustomTable.mutate(selecetedCustomTable?.id, {
        onSuccess: () => {
          setSelectedExtract(
            selectedExtract.filter(item => item.table !== selecetedCustomTable?.name)
          );
          refetch();
          refetchCategories();
          queryClient.refetchQueries([GET_CUSTOM_FIELDS]);
          setIsOpenDeleteCustomTableModal(false);
          callback?.();
        },
        onError: err => {
          toast.error(extractApiError(err, 'Unable to delete this custom table.'));
        }
      });
      return;
    }

    deleteCustomTable.mutate(selecetedCustomTable?.id, {
      onSuccess: () => {
        setSelectedExtract(
          selectedExtract.filter(item => item.table !== selecetedCustomTable?.name)
        );
        refetch();
        refetchCategories();
        queryClient.refetchQueries([GET_CUSTOM_FIELDS]);
        setIsOpenDeleteCustomTableModal(false);
        callback?.();
      },
      onError: err => {
        toast.error(extractApiError(err, 'Unable to delete this custom table.'));
      }
    });
  };

  const debouncedSearchValue = useDebounce(term, 1000);

  const { data: groups, isLoading: isLoadingCompany } = useUserGroups();

  const currentGroupId = groups?.current_company_id;

  const { data: project, isLoading: isLoadingProject } = useGetWorkflowById({
    id,
    params: {
      headers: {
        company_id: currentGroupId
      }
    },
    queryParams: {
      enabled: !!id && !!currentGroupId,
      onSuccess: res => {
        setReportType(res?.report_type);
      }
    }
  });

  const canManageWorkflow =
    currentUser && project
      ? permissionPolicies.checkAccess({
          currentUserRole: currentUser?.role,
          currentUserId: currentUser?.azure_object_id,
          assignedTo: project?.assigned_to
        })
      : true;

  const {
    data,
    isFetching: isFetchingSingleSheet,
    refetch: refetchSheet
  } = useGetSheet({
    sheetId: sheetId,
    headers: {
      'Accept-Encoding': 'gzip'
    },
    params: {
      headers: {
        company_id: currentGroupId,
        project_id: project?.id
      }
    },
    queryParams: {
      enabled: Boolean(sheetId) && Boolean(project?.id),
      select: res => {
        return {
          ...res,
          funds: res?.funds ? res.funds.filter(fund => !fund.is_deleted) : []
        };
      },
      keepPreviousData: true,
      staleTime: CACHE_STALE_TIME
    }
  });

  const {
    data: categories,
    refetch: refetchCategories,
    isFetching: isFetchingCategories,
    isLoading: isLoadingCategories
  } = useGetCategories({
    params: { sheet_id: sheetId },
    queryParams: {
      enabled: !!sheetId,
      keepPreviousData: true,
      staleTime: CACHE_STALE_TIME,
      onSuccess: res => {
        return res.map(el => {
          el.tables = el.tables.map((table, idx) => ({
            ...table,
            id: table.id || table.name + idx + '-table'
          }));
          return el;
        });
      }
    }
  });

  const { data: period } = usePeriodById({
    periodId,
    params: {
      headers: {
        company_id: currentGroupId,
        project_id: project?.id
      }
    },
    queryOptions: {
      enabled: Boolean(periodId) && Boolean(project?.id)
    }
  });

  const { data: fundMappings, refetch: refetchFundMappings } = useGetFundMappings({
    params: {
      period_id: periodId,
      sheet_id: sheetId
    },
    queryParams: {
      enabled: Boolean(data),
      keepPreviousData: true,
      staleTime: CACHE_STALE_TIME
    }
  });

  const { data: extractFile, refetch: refetchExtractedFile } = useGetFileExtract({
    params: {
      sheet_id: sheetId
    },
    queryParams: {
      enabled: Boolean(data),
      keepPreviousData: true,
      staleTime: CACHE_STALE_TIME
    }
  });

  const {
    data: files,
    isFetching: isFetchingPeriodFiles,
    refetch: refetchFiles
  } = usePeriodFiles({
    periodId,
    queryOptions: {
      enabled: !!periodId,
      onSuccess: res => {
        if (!fileId) setFileId(res[0]?.id);
        if (!sheetId) setSheetId(res[0]?.sheets[0]?.id);
      }
    }
  });

  const { refetch, data: savedFiles } = useGetWorkflowExtract({
    params: {
      workflow_id: id,
      period_id: periodId
    },
    queryParams: {
      enabled: !!id,
      keepPreviousData: true,
      staleTime: CACHE_STALE_TIME
    }
  });

  const file = useMemo(() => {
    if (!fileId || !files) return null;
    const currentFile = files.find(_file => _file.id === fileId);

    if (!currentFile) {
      setFileId(files[0]?.id);
      if (files[0]?.sheets?.[0]?.id) setSheetId(files[0]?.sheets?.[0]?.id);

      return files?.[0];
    }

    return currentFile;
  }, [fileId, files]);

  const sheet = useMemo(() => {
    if (!file) return null;
    if (!sheetId) return file.sheets[0];

    return file.sheets.find(_sheet => _sheet.id === sheetId);
  }, [file?.sheets, sheetId]);

  const reportType = project?.report_type;
  const setPasswordToFile = useSetPasswordToFile();

  const isLockedFile = useCallback(
    currentFile => currentFile?.is_password_protected && !currentFile?.has_password,
    []
  );

  useEffect(() => {
    if (!files?.length || !fileId) return;

    const currentFile = files.find(_file => _file.id === fileId);
    if (isLockedFile(currentFile)) {
      setSelectedProtectedFile(prev => (prev?.id === currentFile.id ? prev : currentFile));
      return;
    }

    if (selectedProtectedFile?.id === currentFile?.id && currentFile?.has_password) {
      setSelectedProtectedFile(null);
    }
  }, [fileId, files, isLockedFile, selectedProtectedFile?.id]);

  const { mutate: updateTableStructure } = useUpdateTableStructure({
    sheetId,
    queryParams: {
      onSuccess: () => {
        refetch();
        refetchSheet();
        refetchCategories();
      },
      onError: err => {
        toast.error(extractApiError(err, 'Unable to update the table structure.'));
      }
    }
  });

  const { data: headerFooter, refetch: refetchHeaderFooter } = useGetHeaderFooter({
    sheetId,
    queryParams: {
      enabled: !!sheetId,
      keepPreviousData: true,
      staleTime: CACHE_STALE_TIME
    }
  });

  const { mutate: updateHeaderFooter } = useSetHeaderFooter({
    sheetId,
    queryParams: {
      onSuccess: () => {
        refetchHeaderFooter();
      },
      onError: async err => {
        await toast.remove();
        toast.error(extractApiError(err, 'Unable to update header and footer settings.'));
      }
    }
  });

  const { mutate, isLoading: isLoadingSave } = useCreateSheetExtract();
  // `mutate` not `mutateAsync` — call sites are fire-and-forget with onError.
  const { mutate: mutateAutoExtract, isLoading: isLoadingAutoExtract } = useAutoExtract();

  // Kick off the workflow stepper's polling as soon as the user triggers AI
  // extraction. The stepper polls usePeriodById while any *_progress_status
  // === 'In progress'; flipping the cached period to fresh data is what
  // turns polling on. Without this, the stepper sees "Not started" until the
  // user navigates away and back.
  const kickStepperPolling = () => {
    queryClient.invalidateQueries(['GET_PERIOD_BY_ID']);
  };

  const deleteFile = useDeletePeriodFile();
  const deleteSheet = useDeleteSheet();

  const { setSelectedTableName, setTableNames } = transformationStore;

  const isLastPeriod = project?.periods?.[0]?.id === periodId;

  useEffect(() => {
    if (!period || !currentUser) return;

    if (currentUser?.role === 'Admin') {
      setIsLockedPeriod(false);
      return;
    }

    setIsLockedPeriod(period?.submit_progress_status === 'Completed' || !isLastPeriod);
  }, [period, currentUser, isLastPeriod]);

  const extractedFile = useMemo(() => {
    if (extractId && !category) {
      const _result = extractFile?.find(item => item.sheet_id === sheetId && item.id === extractId);
      if (_result) return _result;
    }

    if (extractId && category) {
      const _result = extractFile?.find(
        item =>
          item.sheet_id === sheetId && item.id === extractId && item.category === category.name
      );
      if (_result) return _result;
    }

    if (category)
      return extractFile?.find(
        item => item.sheet_id === sheetId && item.category === category.name
      );

    const foundExtract = extractFile?.find(item => item.id === extractId);

    if (extractId && foundExtract) {
      return foundExtract;
    }

    return extractFile?.find(item => item.sheet_id === sheetId);
  }, [sheetId, category, extractFile, extractId]);

  const categoryFromExtractedFile = useMemo(() => {
    if (extractedFile && category?.name === extractFile?.category)
      return categories?.find(item => item.name === extractedFile?.category);

    return categories?.find(item => item.name === category?.name);
  }, [categories, extractedFile, category]);

  const {
    data: pdfRegionSelections,
    refetch: refetchPdfRegionSelections,
    isFetching: isFetchingPdfRegionSelections
  } = useGetPdfRegionSelections({
    params: {
      sheet_id: sheetId
    },
    queryParams: {
      enabled: file?.format === 'pdf' && !!sheetId,
      keepPreviousData: true,
      staleTime: CACHE_STALE_TIME
    }
  });

  const upsertPdfRegionSelection = useUpsertPdfRegionSelection();
  const deletePdfRegionSelection = useDeletePdfRegionSelection();

  useEffect(() => {
    if (sheet && categoryFromExtractedFile && fundMappings) {
      if (extractedFile) {
        const updatedExtractFile = getUpdatedExtractedFileFunds(extractedFile, fundMappings);
        computeFunds(categoryFromExtractedFile, fundMappings?.fund_mappings, updatedExtractFile);
        return;
      }
      computeFunds(categoryFromExtractedFile, fundMappings?.fund_mappings, extractedFile);
    } else {
      set({
        funds: []
      });
    }

    return () => {
      setFunds([]);
      clear();
      setPickedFund([]);
      setSelectedFund([]);
      setPickedChildFund([]);
    };
  }, [sheet, extractFile, extractedFile, fundMappings, openTemplates, categoryFromExtractedFile]);

  const replicateExtractionList = useGetReplicateExtraction({
    params: {
      category: category?.name,
      is_structured: data?.is_structured,
      sheet_id: sheetId
    },
    queryParams: {
      staleTime: CACHE_STALE_TIME,
      enabled: !!category?.name && !!sheetId && data?.is_structured !== undefined
    }
  });

  const { zoomLevel, zoomIn, zoomOut } = useTableNavigation();

  // Reset auto-select flag whenever the user switches to a different file so that
  // the first-sheet-with-data selection runs again for the new file.
  const hasAutoSelectedSheetRef = useRef(false);
  const prevFileIdRef = useRef(fileId);
  if (prevFileIdRef.current !== fileId) {
    prevFileIdRef.current = fileId;
    hasAutoSelectedSheetRef.current = false;
  }

  // Keep a ref to sheetId so the auto-select effect always reads the current value
  // without needing sheetId in its dependency array (which would re-run on every
  // manual sheet selection).
  const sheetIdRef = useRef(sheetId);
  sheetIdRef.current = sheetId;

  const { data: statuses, refetch: refetchStatuses } = useGetAllStatusExtractions({
    params: {
      file_id: fileId
    },
    queryParams: {
      enabled: Boolean(sheetId),
      // SignalR (AutomationExtractionStatusUpdate) drives live status
      // refreshes — see the listener below. 60s heartbeat is the safety
      // net for missed messages. The `isPauseStatusReq` gate halts both
      // paths once the extraction reaches a terminal status.
      refetchInterval: isPauseStatusReq ? 0 : 60000,
      onSuccess: res => {
        const status = res?.find(
          item =>
            item.sheet_id === sheetId &&
            item.file_id === file?.id &&
            item.category === category?.name
        );
        if (res.length === 0) setIsPauseStatusReq(true);
        if (status?.status === 'failed' || status?.status === 'cancelled') {
          setIsPauseStatusReq(true);
        }
        if (status?.status === 'completed') {
          refetchExtractedFile();
          refetch();
          setIsPauseStatusReq(true);
        }
      }
    }
  });

  const handleExtractionStatusUpdate = useCallback(
    data => {
      if (data?.period_id && data.period_id !== periodId) return;
      if (isPauseStatusReq) return;
      refetchStatuses();

      if (!data?.sheet_id || data.sheet_id === sheetId) {
        refetchExtractedFile();
        refetch();
      }
    },
    [periodId, isPauseStatusReq, sheetId, refetchStatuses, refetchExtractedFile, refetch]
  );

  useSignalRListener(SIGNALR_LISTENERS.ON_EXTRACTION_STATUS_UPDATED, handleExtractionStatusUpdate);
  useSignalRListener(SIGNALR_LISTENERS.ON_FUND_EXTRACTION_UPDATED, handleExtractionStatusUpdate);
  useSignalRListener(
    SIGNALR_LISTENERS.ON_AUTOMATION_EXTRACTION_STATUS_UPDATED,
    handleExtractionStatusUpdate
  );

  // Transformation tab fetches via `useGetTransforms`/`useGetTransform`
  // (both keyed by GET_TRANSFORM). Invalidating the family refetches the
  // active tab's data; the chrome status sync already refreshes the period
  // record so the stepper transitions out of "In progress" in parallel.
  const handleTransformationStatusUpdate = useCallback(
    data => {
      if (data?.period_id && data.period_id !== periodId) return;
      queryClient.invalidateQueries([GET_TRANSFORM]);
    },
    [periodId]
  );

  useSignalRListener(
    SIGNALR_LISTENERS.ON_TRANSFORMATION_STATUS_UPDATED,
    handleTransformationStatusUpdate
  );
  useSignalRListener(
    SIGNALR_LISTENERS.ON_AUTOMATION_TRANSFORMATION_STATUS_UPDATED,
    handleTransformationStatusUpdate
  );
  useSignalRListener(SIGNALR_LISTENERS.ON_TRANSFORMATION_UPDATED, handleTransformationStatusUpdate);

  // FileProcessingUpdate covers file/sheet lifecycle events (upload, parse,
  // delete, password detection). Drives the period's file list refresh so
  // delete-file / delete-sheet mutations don't need a manual refetchFiles.
  useSignalRListener(SIGNALR_LISTENERS.ON_FILE_PROCESSING_UPDATED, data => {
    if (data?.period_id && data.period_id !== periodId) return;
    refetchFiles();
  });

  const { mutate: deleteMutate, isLoading: isLoadingDeleteExtract } = useDeleteSheetExtract();

  // Auto-select the first sheet with extraction data on initial load (or after a file change).
  // Runs as an effect so it never triggers a state update during render.
  // sheetId is read from sheetIdRef (always current) so the effect does not need it as a
  // dependency and won't re-run on every manual sheet selection.
  useEffect(() => {
    if (hasAutoSelectedSheetRef.current) return;
    if (!file?.sheets?.length || !statuses) return;

    const currentSheetId = sheetIdRef.current;
    const currentSheetHasStatus = statuses.some(item => item.sheet_id === currentSheetId);
    if (!currentSheetHasStatus) {
      const firstWithData = file.sheets.find(s => statuses.some(item => item.sheet_id === s.id));
      if (firstWithData && firstWithData.id !== currentSheetId) {
        setSheetId(firstWithData.id);
      }
    }

    hasAutoSelectedSheetRef.current = true;
  }, [file?.sheets, statuses]);

  const extractStatus = statuses?.find(
    item =>
      item.sheet_id === sheetId && item.file_id === file?.id && item.category === category?.name
  );

  const isIndividualFund =
    data?.is_per_fund ||
    fundMappings?.fund_mappings?.length === 1 ||
    (fundMappings?.fund_mappings ?? fundMappings)?.filter(mapp => !!mapp.id)?.length === 1;

  const { mutate: calcelAutoExtract, isLoading: isLoadingCancel } = useCanceAutoExtract();

  const { mutate: mutateUpdate, isLoading: isLoadingUpdate } = useUpdateSheetExtract();

  const onChangeTableStructure = val => {
    if (val === data?.is_structured) return;

    updateTableStructure({
      is_structured: val
    });
  };

  const onCanceAutoExtract = (id, onSuccessFn) => {
    calcelAutoExtract(id, {
      onSuccess: () => {
        // Backend fires AutomationExtractionStatusUpdate with status='cancelled'
        // — the page's SignalR listener refetches statuses for us.
        toast.success('Extraction canceled!');
        setActionDelete(null);
        onSuccessFn && onSuccessFn();
      },
      onError: err => {
        toast.error(extractApiError(err, 'Unable to cancel the extraction task.'));
      }
    });
  };

  const onChangeTab = async value => {
    if (hasChanges) {
      const userResponse = await openPopup();

      if (!userResponse) return;
    }

    if (activeTab === 'extraction') {
      previousExtractionState.current = { categoryName, extractId };
    }

    if (value === 'extraction' && previousExtractionState.current.categoryName) {
      setSearchParams(init => {
        init.set('tab', value);
        init.set('category', previousExtractionState.current.categoryName);
        if (previousExtractionState.current.extractId) {
          init.set('extractId', previousExtractionState.current.extractId);
        } else {
          init.delete('extractId');
        }
        init.delete('extractedTransformationTable');
        return init;
      });
      setTerm('');
      return;
    }

    setActiveTab(value);
    setTerm('');
  };

  const onActionDelete = value => {
    if (value) setActionDelete(value);
    else {
      if (actionDelete?.type === 'delete-extract') setOpenTemplates(true);
      setActionDelete(null);
    }
  };

  const onChangeHeaderFooter = useCallback(
    (footerIdx, headerIdx) => {
      if (footerIdx === headerFooter?.footer_idx && headerIdx === headerFooter?.headerIndex) return;

      const activeTableLength =
        file?.format === 'pdf' ? data?.data?.length || 0 : data?.data?.length || 0;

      updateHeaderFooter({
        header_idx: Number(headerIdx) - 1 < 0 ? 0 : Number(headerIdx) - 1,
        footer_idx: Number(footerIdx) - 1 < 0 ? activeTableLength - 1 : Number(footerIdx) - 1
      });
    },
    [headerFooter, file?.format, data?.data?.length]
  );

  const onEnableValue = (foundChildId, value) => {
    if (
      activeFundChildValue &&
      activeFundChildValue.id === value?.id &&
      activeFundChildValue.foundChildId === foundChildId
    ) {
      setActiveFundChildValue(null);
    } else {
      setActiveFundChildValue({
        foundChildId,
        ...value
      });
    }
    onClearSearch();
  };

  const extractionTableDataResponse = useMemo(() => {
    if (file?.format !== 'pdf') return data;

    if (!pdfRegionSelections?.length) {
      return {
        ...data,
        data: [],
        pdf_region_preview: null
      };
    }

    return {
      ...data,
      data: data?.data || [],
      pdf_region_preview: {
        csv: buildCsvFromRows(data?.data || [])
      }
    };
  }, [file?.format, data, pdfRegionSelections]);

  const onSearchFromData = () => {
    setActiveFundChildValue(null);
    const _arr =
      activeTab === 'extraction' ? extractionTableDataResponse?.data : transformationData;

    const _items = [];
    for (let i = 0; i < _arr?.length; i++) {
      const value = _arr[i];
      const rows = Object.values(value);
      for (let j = 0; j < rows.length; j++) {
        const column = rows[j];
        if (String(column).toLowerCase().includes(debouncedSearchValue.toLowerCase())) {
          _items.push({
            rowIndex: i + 1,
            columnIndex: j + 1
          });
        }
      }
    }

    setFoundItems(_items);
    if (_items.length > 0) {
      setActiveSearchedIndex(0);
      setFoundCell(_items[0]);
    }
  };

  usePromptUnloadPage(hasChanges);

  const onSearchToUp = () => {
    setActiveFundChildValue(null);
    if (activeSearchedIndex > 0) {
      setFoundCell(foundItems[activeSearchedIndex - 1]);
      setActiveSearchedIndex(prev => prev - 1);
    } else {
      setActiveSearchedIndex(foundItems.length - 1);
      setFoundCell(foundItems[foundItems.length - 1]);
    }
  };

  const onSearchToDown = () => {
    setActiveFundChildValue(null);
    if (activeSearchedIndex === foundItems.length - 1) {
      setActiveSearchedIndex(0);
      setFoundCell(foundItems[0]);
      return;
    }
    setFoundCell(foundItems[activeSearchedIndex + 1]);
    setActiveSearchedIndex(prev => prev + 1);
  };

  useEffect(() => {
    if (debouncedSearchValue) {
      onSearchFromData();
    } else {
      setFoundItems([]);
    }
  }, [debouncedSearchValue, extractionTableDataResponse?.data, transformationData]);

  const onSearch = value => {
    setTerm(value);
  };

  const onFocusLocation = useCallback(value => {
    setFoundCell(value);
    setTimeout(() => {
      setFoundCell(null);
    }, 2000);
  }, []);

  const onSavePdfRegionSelection = useCallback(
    (selection, { onSuccess } = {}) => {
      if (!sheetId) {
        toast.error('Open a PDF sheet before saving a region.');
        return;
      }

      upsertPdfRegionSelection.mutate(
        {
          sheet_id: sheetId,
          page_number: selection.pageNumber,
          bbox: selection.bbox
        },
        {
          onSuccess: async () => {
            await refetchPdfRegionSelections();
            await refetchSheet();
            await refetchHeaderFooter();
            toast.success('PDF table region saved.');
            onSuccess?.();
          },
          onError: err => {
            toast.error(extractApiError(err, 'Unable to save this PDF table region.'));
          }
        }
      );
    },
    [sheetId, refetchPdfRegionSelections]
  );

  const onDeletePdfRegionSelection = useCallback(
    selectionId => {
      if (deletePdfRegionSelection.isLoading) return;

      deletePdfRegionSelection.mutate(selectionId, {
        onSuccess: async () => {
          await refetchPdfRegionSelections();
          await refetchSheet();
          await refetchHeaderFooter();
          toast.success('PDF region removed.');
        },
        onError: err => {
          toast.error(extractApiError(err, 'Unable to remove this PDF region.'));
        }
      });
    },
    [deletePdfRegionSelection.isLoading, refetchPdfRegionSelections, refetchSheet]
  );

  const replicateAutoExtract = useReplicateAutoExtract();

  const onReplicateAutoExtract = (extractionId, applyTransformation, onSuccess) => {
    const sourceFunds = fundMappings?.fund_mappings?.filter(item => !!item.source_fund);

    if (
      sourceFunds?.length === 0 &&
      category?.name !== 'Lookup' &&
      reportType !== REPORT_TYPES.CUSTOM
    ) {
      setIsOpenFundMapping(true);
      return;
    }

    replicateAutoExtract.mutate(
      {
        data: {
          extraction_id: extractionId,
          sheet_id: sheetId,
          apply_transformation: !!applyTransformation
        }
      },
      {
        onSuccess: () => {
          // Keep refetchStatuses for the instant "Extraction started" row
          // and refetchCategories for the category/custom-fields scope the
          // user just replicated into. refetch (savedFiles) +
          // refetchExtractedFile + GET_CUSTOM_FIELDS are re-driven by the
          // status query's onSuccess cascade once SignalR pushes completion.
          refetchStatuses();
          refetchCategories();
          toast.success('Extraction started!');
          setIsPauseStatusReq(false);
          if (onSuccess) onSuccess();
        },
        onError: err => {
          toast.error(extractApiError(err, 'Unable to reuse this extraction setup.'));
        }
      }
    );
  };

  const [selectedFields, setSelectedFields] = useState([]);

  const onChangeAction = async (value, fields, onSuccess) => {
    if (!category) return toast.error('Please, select category!');

    const sourceFunds = fundMappings?.fund_mappings?.filter(item => !!item.source_fund);

    const sheetWithCategory = extractFile?.find(
      item => item.sheet_id === sheetId && item.category === category.name
    );

    if (hasChanges) {
      const userResponse = await openPopup();

      if (!userResponse) return;
    }

    if (!category.is_fund && value === 'SemiAutomatic') onOpenSemiExtract();

    // error toast to fill all source funds if it is empty
    if (category.is_fund && sourceFunds?.length === 0) {
      setIsOpenFundMapping(true);
      setFundMappingAction(value);
      toast.error("Fund mapping doesn't exist, please map the funds.");
      return 'not-mapped-funds';
    }

    // warning popup about to fill all source funds

    if (
      category.is_fund &&
      fundMappings?.fund_mappings?.some(item => !!item.source_fund) &&
      fundMappings?.fund_mappings?.length !== sourceFunds?.length &&
      !fields
    ) {
      setIsOpenFundMapping(true);
      setFundMappingAction(value);
      toast.error('Not all funds are mapped');
    } else if (value === 'SemiAutomatic') {
      onOpenSemiExtract();
    }

    if (isLoadingAutoExtract && value === 'Automatic') return;

    if (extractStatus?.status === 'in progress' && value === 'Manual') {
      setActionDelete({
        type: 'cancel-in-progress-auto-extraction',
        ...extractStatus
      });

      return;
    }

    if (value === 'Automatic' && fields) {
      if (sheetWithCategory && value === 'Automatic') {
        setSelectedFields(fields);

        return onActionDelete({
          type: 'reset-autoextraction'
        });
      }

      mutateAutoExtract(
        {
          category: category?.name,
          sheet_id: sheetId,
          tables: fields
        },
        {
          onSuccess: () => {
            // Keep refetchStatuses for the instant "Extraction started" row;
            // refetchExtractedFile + refetch (savedFiles) are re-driven by the
            // status query's onSuccess cascade once SignalR pushes completion.
            refetchStatuses();
            kickStepperPolling();
            toast.success('Extraction started!');
            setIsPauseStatusReq(false);
            setSelectedFields([]);
            if (onSuccess) onSuccess();
          },
          onError: err => {
            toast.error(
              extractApiError(err, 'Unable to start extraction with the selected setup.')
            );
          }
        }
      );
    }

    setAction(value === 'SemiAutomatic' ? undefined : value);
  };

  const nextToExtract = async () => {
    try {
      const _fundMappings = await refetchFundMappings();

      const sourceFunds = _fundMappings?.data?.fund_mappings?.filter(item => !!item.source_fund);

      if (category.is_fund && sourceFunds?.length === 0) {
        return onChangeAction(fundMappingAction);
      }

      if (fundMappingAction === 'SemiAutomatic') {
        onOpenSemiExtract();
      } else {
        setAction(fundMappingAction);
      }

      setFundMappingAction(null);
    } catch (e) {
      toast.error(extractApiError(e));
    }
  };

  const onOpenTemplates = () => {
    setOpenTemplates(prev => !prev);
  };

  const currentExtract = extractFile?.find(item => item.id === extractId) || extractFile?.[0];

  useEffect(() => {
    if (activeTab !== 'extraction' || !currentExtract) return;

    const shouldSyncFromCurrentExtract = Boolean(extractId) || !categoryName;
    if (!shouldSyncFromCurrentExtract) return;

    if (currentExtract?.category) {
      if (categoryName !== currentExtract.category) {
        setSearchParams(init => {
          init.set('category', currentExtract.category);
          return init;
        });
      }

      if (categories?.length) {
        const matchedCategory = categories.find(item => item.name === currentExtract.category);

        if (matchedCategory) {
          setCategory(matchedCategory);
          setPreviusCategory(prev => {
            if (prev[sheetId]?.name === matchedCategory.name) return prev;

            return {
              ...prev,
              [sheetId]: matchedCategory
            };
          });
        }
      }
    }

    previousExtractionState.current = {
      categoryName: currentExtract?.category || null,
      extractId: currentExtract?.id || null
    };
  }, [
    activeTab,
    categoryName,
    categories,
    currentExtract,
    extractId,
    setCategory,
    setSearchParams,
    sheetId
  ]);

  useEffect(() => {
    if (extractId) return;

    setExtractId(extractFile?.find(item => item.sheet_id === sheetId)?.id);
  }, [extractId, activeTab, extractFile, sheetId]);

  const onSelectExtractCategory = extrct => {
    if (!extrct) return;

    const id = extrct.id || extrct;
    setExtractId(id);

    const selected = extractFile?.find(item => item.id === id);
    if (selected?.category) {
      previousExtractionState.current = { categoryName: selected.category, extractId: id };
    }
  };

  const onClearSearch = () => {
    setTerm('');
    setFoundItems([]);
    setFoundCell(null);
  };

  const onSelectFile = async value => {
    if (hasChanges) {
      const userResponse = await openPopup();

      if (!userResponse) return;
    }

    if (value?.id === fileId) return;

    setSelectedExtract([]);
    setFileId(value?.id);
    if (!isLockedFile(value) && value?.sheets?.[0]?.id) {
      setSheetId(value?.sheets[0]?.id);
    }
    onClearSearch();
    setCategory(null);
    setSelectedTableName(null);
    setExtractionType(null);
    setTableNames([]);
    setFunds([]);
    clear();
    setSelectedFund([]);
    setPickedChildFund([]);
    setPickedFund([]);
    setAction();
    setIsPauseStatusReq(false);
    setExtractedTransformationTable('');

    if (isLockedFile(value)) {
      setSelectedProtectedFile(value);
      return;
    }

    if (value?.sheets?.[0]?.id && previuseCategory[value.sheets[0].id]) {
      setCategory(previuseCategory[value.sheets[0].id]);
    }
  };

  const onUnlockProtectedFile = useCallback(
    pwd => {
      if (!selectedProtectedFile?.id) return;

      setPasswordToFile.mutate(
        {
          periodId,
          fileId: selectedProtectedFile.id,
          data: {
            password: pwd
          }
        },
        {
          onSuccess: async () => {
            const refreshedFiles = await refetchFiles();
            const unlockedFile = refreshedFiles?.data?.find(
              _file => _file.id === selectedProtectedFile.id
            );

            if (unlockedFile?.id) {
              setFileId(unlockedFile.id);
              if (unlockedFile?.sheets?.[0]?.id) setSheetId(unlockedFile.sheets[0].id);
            }

            setSelectedProtectedFile(null);
            toast.success('Successfully unlocked');
          },
          onError: err => {
            toast.error(extractApiError(err, 'Unable to unlock this file.'));
          }
        }
      );
    },
    [periodId, refetchFiles, selectedProtectedFile, setPasswordToFile]
  );

  const onCreateCustomTable = (value, callback) => {
    if (project?.report_type === REPORT_TYPES.CUSTOM) {
      createGlobalCustomTable.mutate(
        {
          categoryId: category?.id,
          data: {
            name: value,
            global_custom_fields: []
          }
        },
        {
          onSuccess: () => {
            refetch();
            refetchCategories();
            refetchExtractedFile();
            toggleCustomTableDrawer();
            callback?.();
            toast.success('Successfully created new Custom Table');
          },
          onError: err => {
            toast.error(extractApiError(err, 'Unable to create this custom table.'));
          }
        }
      );
    } else
      createCustomTable.mutate(
        {
          name: value,
          sheet_id: sheetId,
          category: 'Lookup'
        },
        {
          onSuccess: () => {
            refetch();
            refetchCategories();
            refetchExtractedFile();
            toggleCustomTableDrawer();
            callback?.();
            toast.success('Successfully created new Custom Table');
          },
          onError: err => {
            toast.error(extractApiError(err, 'Unable to create this custom table.'));
          }
        }
      );
  };

  const onUpdateCustomTable = (value, callback) => {
    updateCustomTable.mutate(
      {
        id: selecetedCustomTable.id,
        data: { name: value }
      },
      {
        onSuccess: () => {
          refetch();
          refetchCategories();
          refetchExtractedFile();
          toggleUpdateCustomTableDrawer();
          callback?.();
          toast.success('Custom Table successfully updated');
        },
        onError: err => {
          toast.error(extractApiError(err, 'Unable to update this custom table.'));
        }
      }
    );
  };

  const onSelectSheet = async value => {
    if (hasChanges) {
      const userResponse = await openPopup();

      if (!userResponse) return;
    }

    if (value?.id === sheetId) return;

    setSelectedExtract([]);
    setExtractionType(null);
    setSheetId(value?.id);
    setCategory(null);
    setSelectedTableName(null);
    setTableNames([]);
    setAction();
    setFunds([]);
    clear();
    setSelectedFund([]);
    setPickedChildFund([]);
    setPickedFund([]);
    setIsPauseStatusReq(false);
    onClearSearch();
    setExtractedTransformationTable('');

    if (previuseCategory[value.id]) {
      setCategory(previuseCategory[value.id]);
    }
  };

  function openPopup(msg = 'You have unsaved data. Are you sure you want to proceed?') {
    return new Promise(resolve => {
      // Simulate opening popup
      setTimeout(() => {
        const userResponse = confirm(msg);

        if (userResponse) {
          clear();
          setHasChanges(false);
        }

        resolve(userResponse);
      }, 100);
    });
  }

  const onChangeCategory = async value => {
    if (hasChanges) {
      const userResponse = await openPopup();

      if (!userResponse) return;
    }

    setSelectedExtract([]);

    const foundExtract = extractFile?.find(item => item.category === value.name);

    if (foundExtract?.id) setExtractId(foundExtract.id);

    setCategoryName(value?.name);
    setCategory(value);
    setPreviusCategory(prev => ({
      ...prev,
      [sheetId]: value
    }));
  };

  useEffect(() => {
    if (isFetchingCategories || isLoadingCategories) return;

    if (categoryName && categories) {
      const _category = categories.find(item => item.name === categoryName);
      setCategory(_category);
    }
  }, [categoryName, categories, isFetchingCategories, isLoadingCategories]);

  const onChangeFunds = useCallback(value => {
    setHasChanges(true);
    setFunds(value);
    set({
      funds: value
    });
    //addAction(value);
  }, []);

  const onDeleteExtractSheet = extractId => {
    deleteMutate(extractId, {
      onSuccess: () => {
        // Backend fires AutomationExtractionStatusUpdate on delete-extract
        // — the page's SignalR listener refetches statuses, which then
        // cascades into refetchExtractedFile + refetch (savedFiles) via the
        // status query's onSuccess.
        setActionDelete(null);
        setOpenTemplates(true);
      },
      onError: err => {
        toast.error(extractApiError(err, 'Unable to delete this extraction.'));
      }
    });
  };

  const onConfirmDelete = () => {
    if (actionDelete.type === 'save-extraction') {
      onSubmit();
    }
    if (actionDelete.type === 'delete-extract') onDeleteExtractSheet(actionDelete.id);

    if (actionDelete.type === 'reset-autoextraction')
      mutateAutoExtract(
        {
          category: category?.name,
          sheet_id: sheetId,
          tables: selectedFields
        },
        {
          onSuccess: () => {
            // refetchExtractedFile + refetch (savedFiles) are re-driven by
            // the status query's onSuccess cascade once SignalR pushes
            // completion. Only the immediate "Extraction started" row
            // needs a synchronous status refetch.
            refetchStatuses();
            kickStepperPolling();
            toast.success('Extraction started!');
            setIsPauseStatusReq(false);
            setActionDelete(null);
            // setSelectedFields([]);
          },
          onError: err => {
            toast.error(
              extractApiError(err, 'Unable to restart extraction with the selected fields.')
            );
          }
        }
      );

    if (actionDelete.type === 'cancel-auto-extraction') onCanceAutoExtract(actionDelete.id);
    if (actionDelete.type === 'cancel-in-progress-auto-extraction') {
      const onSuccessFn = () => {
        // setAction('Manual');
      };
      onCanceAutoExtract(actionDelete.id, onSuccessFn);
    }
    if (actionDelete.type === 'delete-file') {
      deleteFile.mutate(
        {
          periodId: periodId,
          fileId: actionDelete?.id
        },
        {
          onSuccess: () => {
            // FileProcessingUpdate listener above refetches the files list.
            setActionDelete(null);
            return 'Successfuly deleted!';
          },
          onError: err => {
            toast.error(extractApiError(err, 'Unable to delete this file.'));
          }
        }
      );
    }

    if (actionDelete.type === 'delete-sheet') {
      deleteSheet.mutate(actionDelete?.id, {
        onSuccess: () => {
          // FileProcessingUpdate listener above refetches the files list.
          setActionDelete(null);
        },
        onError: err => {
          toast.error(extractApiError(err, 'Unable to delete this sheet.'));
        }
      });
    }

    if (actionDelete.type === 'lose-manual-extraction') {
      if (!extractStatus?.status || extractStatus?.status === 'cancelled') {
        mutateAutoExtract(
          {
            category: category?.name,
            sheet_id: sheetId
          },
          {
            onSuccess: () => {
              refetchStatuses();
              kickStepperPolling();
              setAction('Automatic');
              toast.success('Extraction started!');
              setActionDelete(null);
              setIsPauseStatusReq(false);
            },
            onError: err => {
              toast.error(extractApiError(err, 'Unable to start automatic extraction.'));
            }
          }
        );
      } else {
        setAction('Automatic');
        setActionDelete(null);
      }
    }
  };

  const isLookup = useMemo(() => {
    return !category?.is_fund;
  }, [category]);

  const onSubmit = async () => {
    if (!category) return toast.error('Please, select category!');

    // const userResponse = await openPopup(
    //   'Are you sure you want to save the data?'
    // );

    // if (!userResponse) return;

    const sheetWithCategory = extractFile?.find(
      item => item.sheet_id === sheetId && item.category === category.name
    );

    let body = computeSubmitBody({
      funds,
      isLookup,
      isStructured: data?.is_structured,
      header_idx: headerFooter?.header_idx
        ? headerFooter?.header_idx + 1
        : headerFooter?.header_idx,
      footer_idx: headerFooter?.footer_idx
        ? headerFooter?.footer_idx + 1
        : headerFooter?.footer_idx,
      category,
      isIndividualFund: isIndividualFund
    });

    if (sheetWithCategory) {
      body = {
        ...body,
        category: category.name,
        extractId: sheetWithCategory.id,
        sheet_id: sheetId
      };
    } else {
      body = {
        ...body,
        category: category.name,
        sheet_id: sheetId
      };
    }

    if (sheetWithCategory) {
      mutateUpdate(body, {
        onSuccess: () => {
          refetch();
          refetchExtractedFile();
          refetchCategories();
          onOpenTemplates();
          setHasChanges(false);
          setTimeout(() => {
            onOpenTemplates();
          }, 5000);
          setActionDelete(null);
        },
        onError: err => {
          toast.error(extractApiError(err, 'Unable to save this extraction.'));
        }
      });
    } else {
      mutate(body, {
        onSuccess: () => {
          refetch();
          refetchExtractedFile();
          refetchCategories();
          onOpenTemplates();
          setHasChanges(false);
          setTimeout(() => {
            onOpenTemplates();
          }, 5000);
          setActionDelete(null);
        },
        onError: err => {
          toast.error(extractApiError(err, 'Unable to save this extraction.'));
        }
      });
    }
  };

  const onClickNextBtn = () => {
    navigate(`/output-tables/${id}/${periodId}`);
  };

  const onCloseFundMapping = shouldClearAction => {
    setIsOpenFundMapping(prev => !prev);
    refetchFundMappings();
    if (shouldClearAction) setFundMappingAction(null);
    if (extractionType?.id === 'SemiAutomatic') onOpenSemiExtract();
  };

  const onEdit = (id, _categoryId) => {
    //setCategory(null);
    const file = files.find(item => item.sheets.find(sheet => sheet.id === id));
    const sheet = file.sheets.find(sheet => sheet.id === id);

    setFileId(file?.id);
    setSheetId(sheet?.id);
    setExtractId(_categoryId);
    onChangeTab('extraction');

    if (_categoryId) setCategory(categories?.find(el => el.id === _categoryId) || null);
  };

  // preventing refresh page and data loss
  unstable_usePrompt({
    message: 'You have unsaved message. Are you sure want to proceed?',
    when: ({ currentLocation, nextLocation }) =>
      hasChanges && currentLocation.pathname !== nextLocation.pathname
  });

  const isFundLevel = period?.submit_progress_status === 'Not started';

  const hasFundMapping = useMemo(() => {
    return !!fundMappings?.fund_mappings?.find(el => el.id && el.source_fund);
  }, [fundMappings?.fund_mappings]);

  const hasSemiExtraction = replicateExtractionList?.data?.length > 0;

  const onExtractSelectedFields = useCallback(() => {
    let _fields;

    if (isLookup || reportType === REPORT_TYPES.CUSTOM) {
      const selectedCategory = categories?.find(cat => cat.name === category?.name);

      _fields = [...selectedExtract]?.reduce((prev, curr) => {
        const currIndex = prev.findIndex(el => el.name === curr.table);
        const existsTable = !!(category?.tables || selectedCategory?.tables)?.find(
          table => table.name === curr.table
        );

        if (!existsTable) return prev;

        if (typeof currIndex === 'number' && !prev[currIndex]) {
          prev.push({
            name: curr.table,
            extraction_fields: [
              {
                name: curr.name
              }
            ]
          });
        } else {
          prev[currIndex].extraction_fields.push({ name: curr.name });
        }

        return prev;
      }, []);
    } else {
      _fields = [
        {
          name: category.name,
          extraction_fields: [...selectedExtract.map(ext => ({ name: ext.name })).filter(el => el)]
        }
      ];
    }

    onChangeAction('Automatic', _fields, () => {});
  }, [selectedExtract, category, isLookup, reportType, onChangeAction, categories]);

  const extractionResults = useMemo(() => {
    const _sheetStatuses = {};
    const _categoryStatuses = {};
    const statusPriority = {
      'in progress': 3,
      completed: 2,
      failed: 1
    };
    if (statuses) {
      statuses.forEach(item => {
        if (
          _sheetStatuses[item.sheet_id] &&
          statusPriority[_sheetStatuses[item.sheet_id]] < statusPriority[item.status]
        ) {
          _sheetStatuses[item.sheet_id] = item.status;
        } else if (!_sheetStatuses[item.sheet_id]) {
          _sheetStatuses[item.sheet_id] = item.status;
        }

        if (item.sheet_id === sheetId) {
          if (
            _categoryStatuses[item.category] &&
            statusPriority[_categoryStatuses[item.category]] < statusPriority[item.status]
          ) {
            _categoryStatuses[item.category] = item.status;
          } else if (!_categoryStatuses[item.category]) {
            _categoryStatuses[item.category] = item.status;
          }
        }
      });

      return {
        sheets: _sheetStatuses,
        categories: _categoryStatuses
      };
    } else {
      return {
        sheets: {},
        categories: {}
      };
    }
  }, [statuses, sheetId]);

  useEffect(() => {
    if (activeTab === 'extraction') {
      agentStore.setCurrentScreen(WEB_PAGE_STATE_ENUMS.Extraction);
    } else if (activeTab === 'transformation') {
      agentStore.setCurrentScreen(WEB_PAGE_STATE_ENUMS.Transformation);
    } else {
      agentStore.setCurrentScreen(WEB_PAGE_STATE_ENUMS.LookupTables);
    }
  }, [activeTab]);

  const { setLabels, clearLabels } = useWorkflowChrome();

  useEffect(() => {
    setLabels({
      projectLabel: project?.name,
      periodLabel: period?.year && period?.period ? `${period.year}, ${period.period}` : undefined,
      workflowId: project?.id ?? id,
      periodId,
      activeStageKey:
        activeTab === 'transformation'
          ? 'transformation'
          : activeTab === 'lookup_tables'
            ? 'lookup_tables'
            : 'extraction'
    });
    return () => clearLabels();
  }, [
    project?.id,
    project?.name,
    period?.year,
    period?.period,
    id,
    periodId,
    activeTab,
    setLabels,
    clearLabels
  ]);

  return (
    <>
      <Box id={rootId} p={activeTab === 'lookup_tables' ? '0' : '16'} bg="gray.0" pos="relative">
        {activeTab !== 'lookup_tables' && (
          <Header
            onOpenTemplates={onOpenTemplates}
            openTemplates={openTemplates}
            onSave={() =>
              setActionDelete({
                type: 'save-extraction'
              })
            }
            funds={funds}
            extractedTransformationTable={extractedTransformationTable}
            onChangeExtractedTransformationTable={setExtractedTransformationTable}
            isLoadingButton={isLoadingSave}
            isLoadingCancel={isLoadingCancel}
            activeTab={activeTab}
            onCancelAutoExtract={onActionDelete}
            status={extractStatus}
            isStructured={data?.is_structured}
            onClickNextBtn={onClickNextBtn}
            toggleFundMapping={() => setIsOpenFundMapping(prev => !prev)}
            isHasTemplates={savedFiles?.some(item =>
              item.sheets?.some(item => item.extractions.length > 0)
            )}
            reportType={project?.report_type}
            extractedFile={extractedFile}
            hasSemiExtraction={hasSemiExtraction}
            categories={categories}
            refetchCategories={refetchCategories}
            category={category}
            disabledLookupCategory={hasFundMapping}
            onChangeCategory={onChangeCategory}
            files={files}
            onSelectSheet={onSelectSheet}
            onSelectFile={onSelectFile}
            sheet={sheet}
            file={file}
            onOpenSemiExtract={onOpenSemiExtract}
            onChangeAction={onChangeAction}
            onExtractSelectedFields={onExtractSelectedFields}
            savedFiles={savedFiles}
            onActionDelete={onActionDelete}
            onEdit={onEdit}
            extractionResults={extractionResults}
            canUndo={canUndo}
            onSelectExtractCategory={onSelectExtractCategory}
            extractId={extractId}
            extractFile={extractFile}
            workflowId={project?.id}
            isLoadingFiles={isFetchingPeriodFiles}
            periodId={periodId}
            currentUser={currentUser}
            project={project}
          />
        )}
        <Box
          id={`${rootId}-content`}
          mt={activeTab === 'lookup_tables' ? '0' : '12px'}
          pos="relative"
        >
          {activeTab === 'lookup_tables' ? (
            <LookupTables
              periodId={periodId}
              reportType={project?.report_type}
              canManageWorkflow={canManageWorkflow}
            />
          ) : (
            <GridTable
              key={[activeTab, sheetId, file?.id].join('|')}
              file={file}
              foundCell={foundCell}
              zoomIn={zoomIn}
              zoomOut={zoomOut}
              zoomLevel={zoomLevel}
              hasChanges={hasChanges}
              openPopup={openPopup}
              onSelectExtractCategory={onSelectExtractCategory}
              isLoading={isFetchingSingleSheet || isFetchingPeriodFiles || isLoadingCompany}
              sheet={sheet}
              project={project}
              quarter={period?.period}
              periodId={periodId}
              isFundLevel={isFundLevel}
              id={id}
              categories={categories}
              refetchCategories={refetchCategories}
              year={period?.year}
              response={extractionTableDataResponse}
              changeActiveTab={setActiveTab}
              isIndividualFund={isIndividualFund}
              funds={funds}
              transformationData={transformationData}
              setTransformationData={setTransformationData}
              setFunds={setFunds}
              onChangeCategory={onChangeCategory}
              category={category}
              action={action}
              templateType={period?.template_type}
              transformationProgressStatus={period?.transformation_progress_status}
              onChangeAction={onChangeAction}
              onFocusLocation={onFocusLocation}
              onSearch={onSearch}
              foundItems={foundItems}
              isLockedPeriod={isLockedPeriod}
              onSearchToUp={onSearchToUp}
              onSearchToDown={onSearchToDown}
              activeSearchedIndex={activeSearchedIndex}
              term={term}
              extractFile={extractFile}
              refetchSheet={refetchSheet}
              onEnableValue={onEnableValue}
              activeFundChildValue={activeFundChildValue}
              setActiveFundChildValue={setActiveFundChildValue}
              selectedFund={selectedFund}
              setPickedChildFund={setPickedChildFund}
              pickedChildFund={pickedChildFund}
              pickedFund={pickedFund}
              setPickedFund={setPickedFund}
              setSelectedFund={setSelectedFund}
              activeTab={activeTab}
              onChangeFunds={onChangeFunds}
              currentExtract={currentExtract}
              onClearSearch={onClearSearch}
              refetchExtractedFile={refetchExtractedFile}
              refetchFundMappings={refetchFundMappings}
              isLookup={isLookup}
              excelTableRef={excelTableRef}
              fundMappings={fundMappings}
              onRedo={redo}
              onUndo={undo}
              isUndoDisabled={!canUndo}
              isRedoDisabled={!canRedo}
              extractedTransformationTable={extractedTransformationTable}
              onChangeExtractedTransformationTable={setExtractedTransformationTable}
              footerIndex={
                headerFooter?.footer_idx || headerFooter?.footer_idx === 0
                  ? Number(headerFooter?.footer_idx) + 1
                  : headerFooter?.footer_idx
              }
              headerIndex={
                headerFooter?.header_idx || headerFooter?.header_idx === 0
                  ? Number(headerFooter?.header_idx) + 1
                  : headerFooter?.header_idx
              }
              onChangeHeaderFooter={onChangeHeaderFooter}
              onChangeTableStructure={onChangeTableStructure}
              onReplicateAutoExtract={onReplicateAutoExtract}
              isLoadingAutoExtract={isLoadingAutoExtract}
              pdfRegionSelections={pdfRegionSelections}
              pdfViewMode={pdfView}
              onChangePdfViewMode={setPdfView}
              onSavePdfRegionSelection={onSavePdfRegionSelection}
              onDeletePdfRegionSelection={onDeletePdfRegionSelection}
              isSavingPdfRegionSelection={upsertPdfRegionSelection.isLoading}
              isDeletingPdfRegionSelection={deletePdfRegionSelection.isLoading}
              isLoadingPdfRegionPreview={isFetchingPdfRegionSelections || isFetchingSingleSheet}
              openCustomTableDrawer={toggleCustomTableDrawer}
              openUpdateCustomTableDrawer={toggleUpdateCustomTableDrawer}
              onSelectCustomTable={onSelectCustomTable}
              canManageWorkflow={canManageWorkflow}
              reportType={project?.report_type}
            />
          )}

          {activeTab !== 'lookup_tables' && extractStatus?.status === 'in progress' && (
            <Overlay id={`${rootId}-overlay`} color="#fff" backgroundOpacity={0.5} />
          )}
        </Box>
      </Box>

      <Modal
        id={`${rootId}-delete-modal`}
        opened={!!actionDelete?.type}
        radius="sm"
        withCloseButton
        onClose={onActionDelete}
        centered
        title="Confirm your choice"
        size="sm"
      >
        <Box id={`${rootId}-delete-modal-content`}>
          <Text id={`${rootId}-delete-modal-text`} c="gray.7" fz="sm" mb="24">
            {
              deleteActions(actionDelete)[actionDelete?.type]?.[
                actionDelete?.type === 'delete-sheet' || actionDelete?.type === 'delete-file'
                  ? 'title'
                  : 'description'
              ]
            }
          </Text>
          <Flex id={`${rootId}-delete-modal-actions`} align="center" gap="16" justify="flex-end">
            <Button
              id={`${rootId}-delete-modal-cancel`}
              onClick={() => onActionDelete()}
              variant="default"
            >
              Cancel
            </Button>
            <Button
              id={`${rootId}-delete-modal-confirm`}
              loading={
                isLoadingDeleteExtract ||
                isLoadingCancel ||
                isLoadingAutoExtract ||
                deleteFile.isLoading ||
                deleteSheet.isLoading ||
                isLoadingUpdate ||
                isLoadingSave
              }
              onClick={onConfirmDelete}
            >
              Yes
            </Button>
          </Flex>
        </Box>
      </Modal>

      <FundMappingModal
        fileName={file?.name}
        sheetName={sheet?.name}
        periodId={periodId}
        sheetId={sheetId}
        isLockedPeriod={isLockedPeriod}
        isPerFund={headerFooter?.is_per_fund}
        refetchHeaderFooter={refetchHeaderFooter}
        isOpen={isOpenFundMapping}
        onClose={onCloseFundMapping}
        nextToExtract={nextToExtract}
        isLookup={isLookup}
        fundMappingAction={fundMappingAction}
      />
      <SemiExtractionDrawer
        period={period?.period}
        isAutomation={period?.template_type === 'from template'}
        transformationProgressStatus={period?.transformation_progress_status}
        year={period?.year}
        workflowName={project?.name}
        isOpen={openedSemiExtract}
        onClose={onCloseSemiExtract}
        replicateExtractionList={replicateExtractionList}
        onReplicateAutoExtract={onReplicateAutoExtract}
      />

      <CustomTableDrawer
        isOpen={isOpenCustomTableDrawer}
        onClose={toggleCustomTableDrawer}
        onSubmit={onCreateCustomTable}
        isLoading={createCustomTable.isLoading || createGlobalCustomTable.isLoading}
      />

      <CustomTableDrawer
        isUpdate={true}
        initialValue={selecetedCustomTable?.name}
        isOpen={isOpenUpdateCustomTableDrawer}
        onClose={toggleUpdateCustomTableDrawer}
        onSubmit={onUpdateCustomTable}
        isLoading={createCustomTable.isLoading || createGlobalCustomTable.isLoading}
      />

      <NewDeleteConfirmModal
        title="Delete Custom Table"
        description={`Are you sure you want to permanently delete ${selecetedCustomTable?.name}?`}
        isOpen={isOpenDeleteCustomTableModal}
        onClose={toggleDeleteCustomTableModal}
        submitLabel="Yes, Delete"
        cancelLabel="Cancel"
        type="delete"
        onConfirm={onDeleteCustomTable}
        isLoading={deleteGlobalCustomTable.isLoading || deleteCustomTable.isLoading}
      />

      <PasswordModal
        idPrefix={`${rootId}-password-modal`}
        isOpen={!!selectedProtectedFile}
        onClose={() => setSelectedProtectedFile(null)}
        title={`
File <span style="color: #276EF1;">“${selectedProtectedFile?.name}”</span> is locked,<br/>
please enter the password`}
        image={protectedFile}
        cancelLabel="Cancel"
        submitLabel="Submit"
        onConfirm={onUnlockProtectedFile}
        isLoading={setPasswordToFile.isLoading}
      />
    </>
  );
});

export default DataExtraction;
